import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import StrategyImageUpload from '@/components/StrategyImageUpload';
import StrategyIconSelect from '@/components/StrategyIconSelect';
import { useStrategy } from '@/hooks/useStrategy';
import { useUpdateStrategy } from '@/hooks/useUpdateStrategy';
import { useStrategyCategories } from '@/hooks/useStrategies';
import type { StrategyFormData, StrategyFormErrors } from '@/types/strategy';

function validateForm(data: StrategyFormData): StrategyFormErrors {
  const errors: StrategyFormErrors = {};

  if (!data.title.trim()) errors.title = 'Title is required';
  else if (data.title.length > 255) errors.title = 'Title must be 255 characters or less';

  if (!data.subtitle.trim()) errors.subtitle = 'Subtitle is required';
  else if (data.subtitle.length > 255) errors.subtitle = 'Subtitle must be 255 characters or less';

  if (!data.description.trim()) errors.description = 'Description is required';

  if (!data.categoryId) errors.categoryId = 'Category is required';

  const sortNum = parseInt(data.sortOrder, 10);
  if (isNaN(sortNum) || sortNum < 0) errors.sortOrder = 'Sort order must be 0 or greater';

  return errors;
}

export default function EditStrategyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: strategyData, isLoading, error } = useStrategy(id!);
  const updateMutation = useUpdateStrategy(id!);
  const { data: categories } = useStrategyCategories();

  const [formData, setFormData] = useState<StrategyFormData | null>(null);
  const [errors, setErrors] = useState<StrategyFormErrors>({});

  useEffect(() => {
    document.title = 'Edit Strategy | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (strategyData) {
      const strategy = strategyData.data;
      const uploadedImage =
        strategy.documentId && strategy.imageUrl
          ? { documentId: strategy.documentId, viewUrl: strategy.imageUrl, fileType: 'jpg' }
          : null;

      setFormData({
        title: strategy.title,
        subtitle: strategy.subtitle,
        description: strategy.description,
        tip: strategy.tip || '',
        image: uploadedImage,
        documentId: strategy.documentId,
        categoryId: strategy.category.id,
        sortOrder: strategy.sortOrder.toString(),
        iconDocumentId: strategy.iconDocumentId,
      });
    }
  }, [strategyData]);

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pine-pine" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-6">
        <p className="text-red-400">Failed to load strategy</p>
        <button
          onClick={() => navigate('/admin/strategies')}
          className="mt-4 text-pine-pine"
        >
          Back to Strategies
        </button>
      </div>
    );
  }

  const handleFieldChange = (field: keyof StrategyFormData, value: unknown) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
    if (errors[field as keyof StrategyFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the form errors');
      return;
    }

    try {
      await updateMutation.mutateAsync(formData);
      toast.success('Strategy updated successfully!');
      navigate(`/admin/strategies/${id}`);
    } catch (err) {
      console.error('Failed to update strategy:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back button */}
      <button
        onClick={() => navigate(`/admin/strategies/${id}`)}
        className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Strategy</span>
      </button>

      <h1 className="text-pine-display font-display text-pine-ink">Edit Strategy</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-red-400">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="e.g. Thankful Thoughts"
              />
              {errors.title && <p className="text-sm text-red-400">{errors.title}</p>}
            </div>

            {/* Subtitle */}
            <div className="space-y-2">
              <Label htmlFor="subtitle">
                Subtitle <span className="text-red-400">*</span>
              </Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                placeholder="e.g. Share three good things together"
              />
              {errors.subtitle && <p className="text-sm text-red-400">{errors.subtitle}</p>}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Description (Read Aloud) <span className="text-red-400">*</span>
              </Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={6}
                className="w-full px-3 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink resize-y focus:outline-none focus:border-pine-pine"
                placeholder="Main body content / instructions..."
              />
              {errors.description && <p className="text-sm text-red-400">{errors.description}</p>}
            </div>

            {/* Tip (optional) */}
            <div className="space-y-2">
              <Label htmlFor="tip">Tip (optional)</Label>
              <textarea
                id="tip"
                value={formData.tip}
                onChange={(e) => handleFieldChange('tip', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink resize-y focus:outline-none focus:border-pine-pine"
                placeholder="Optional tip text..."
              />
              {errors.tip && <p className="text-sm text-red-400">{errors.tip}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Image Card */}
        <Card>
          <CardHeader>
            <CardTitle>Image</CardTitle>
          </CardHeader>
          <CardContent>
            <StrategyImageUpload
              value={formData.image}
              onChange={(image) => {
                setFormData((prev) =>
                  prev ? { ...prev, image, documentId: image?.documentId ?? null } : null
                );
              }}
            />
          </CardContent>
        </Card>

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="categoryId">
                  Category <span className="text-red-400">*</span>
                </Label>
                <select
                  id="categoryId"
                  value={formData.categoryId}
                  onChange={(e) => handleFieldChange('categoryId', e.target.value)}
                  className="w-full px-3 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:outline-none focus:border-pine-pine"
                >
                  <option value="">Select category...</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && <p className="text-sm text-red-400">{errors.categoryId}</p>}
              </div>

              {/* Sort Order */}
              <div className="space-y-2">
                <Label htmlFor="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  min="0"
                  value={formData.sortOrder}
                  onChange={(e) => handleFieldChange('sortOrder', e.target.value)}
                />
                {errors.sortOrder && <p className="text-sm text-red-400">{errors.sortOrder}</p>}
              </div>
            </div>

            {/* Strategy Icon */}
            <StrategyIconSelect
              value={formData.iconDocumentId}
              onChange={(iconDocumentId) => handleFieldChange('iconDocumentId', iconDocumentId)}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="admin-button-primary"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/strategies/${id}`)}
            className="admin-button-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
